import { PageableResult, PageableSearchOptions, Resource, ScreenDto } from "@exibra/shared";
import { ApiService } from "../api/api.service";

class ScreenService extends ApiService {
    /**
     * Gets a screen by its serial number
     * @param serialNumber The serial number of the screen
     * @returns The screen info
     */
    public async getScreen(id: number): Promise<ScreenDto> {
        const res = await this.axios.get<ScreenDto>(`${Resource.Screen}/${id}`);

        return res.data;
    }

    /**
     * Gets a list of screens
     * @param options The options to search the screens
     * @returns The list of screens
     */
    public async getScreens(options: PageableSearchOptions): Promise<PageableResult<ScreenDto>> {
        const res = await this.axios.get<PageableResult<ScreenDto>>(`${Resource.Screen}`, {
            params: options,
        });

        return res.data;
    }

    /**
     * Assigns a template to a screen
     * @param serialNumber The serial number of the screen
     * @param templateId The ID of the template to assign
     * @returns The updated screen info
     */
    public async assignTemplate(screenId: number, templateId?: number): Promise<ScreenDto> {
        const res = await this.axios.post<ScreenDto>(`${Resource.Screen}/${screenId}/template`, {
            templateId,
        });

        return res.data;
    }

    /**
     * Removes the template assignment from a screen
     * @param serialNumber The serial number of the screen
     * @returns The updated screen info
     */
    public async removeTemplate(serialNumber: string): Promise<ScreenDto> {
        const res = await this.axios.delete<ScreenDto>(
            `${Resource.Screen}/${encodeURIComponent(serialNumber)}/template`,
        );

        return res.data;
    }

    /**
     * Updates a screen's information
     * @param screenId The id of the screen
     * @param data The data to update
     * @returns The updated screen info
     */
    public async updateScreen(screenId: number, data: Partial<ScreenDto>): Promise<ScreenDto> {
        const res = await this.axios.patch<ScreenDto>(`${Resource.Screen}/${screenId}`, data);

        return res.data;
    }

    /**
     * Updates the placeholders for a screen's template
     * @param serialNumber The serial number of the screen
     * @param placeholders The placeholder values to update
     * @returns The updated screen info
     */
    public async updateScreenPlaceholders(
        screenId: number,
        templateId: number,
        placeholders: {
            appraiserName: string;
            tunnelNumber: string;
        },
    ): Promise<ScreenDto> {
        const res = await this.axios.patch<ScreenDto>(
            `${Resource.Screen}/${screenId}/placeholders`,
            { templateId, placeholders },
        );

        return res.data;
    }

    /**
     * Deletes multiple screens by their serial numbers
     * @param serialNumbers Array of serial numbers of screens to delete
     * @returns void
     */
    public async deleteScreens(serialNumbers: string[]): Promise<void> {
        // Delete screens one by one since the API expects single screen deletion
        await Promise.all(
            serialNumbers.map((serialNumber) =>
                this.axios.delete(`${Resource.Screen}/${encodeURIComponent(serialNumber)}`),
            ),
        );
    }

    /**
     * Validates a serial number
     * @param serialNumber The serial number to validate
     * @returns Whether the serial number is valid
     */
    public async validateSerialNumber(serialNumber: string): Promise<boolean> {
        const res = await this.axios.get<boolean>(
            `${Resource.Screen}/serial-number/validate?serialNumber=${serialNumber}`,
        );

        return res.data;
    }

    /**
     * Pairs a screen with a tenant
     * @param uuid The serial number of the screen
     * @param tenantId The ID of the tenant to pair with
     * @returns The updated screen info
     */
    public async pairScreen(
        uuid: string,
        tenantId: number,
        name: string,
        serialNumber: string,
    ): Promise<ScreenDto> {
        const res = await this.axios.patch<ScreenDto>(
            `${Resource.Screen}/${encodeURIComponent(uuid)}/pair`,
            { tenantId, name, serialNumber },
        );

        return res.data;
    }

    /**
     * Sends a locate event to a screen to make it visually identifiable
     * @param serialNumber The serial number of the screen to locate
     * @returns The screen info
     */
    public async locateScreen(serialNumber: string): Promise<ScreenDto> {
        const res = await this.axios.post<ScreenDto>(
            `${Resource.Screen}/${encodeURIComponent(serialNumber)}/locate`,
        );

        return res.data;
    }

    /**
     * Check if a screen is currently in locate mode
     * @param serialNumber The serial number of the screen to check
     * @returns Whether the screen is in locate mode
     */
    public async isScreenLocating(serialNumber: string): Promise<boolean> {
        const res = await this.axios.get<boolean>(
            `${Resource.Screen}/${encodeURIComponent(serialNumber)}/locate`,
        );

        return res.data;
    }

    /**
     * Gets the next available serial number for a tenant
     * @param tenantId The ID of the tenant
     * @returns The next available serial number
     */
    public async getNextSerialNumber(tenantId: number): Promise<string> {
        const res = await this.axios.get<string>(
            `${Resource.Screen}/next-serial-number?tenantId=${tenantId}`,
        );
        return res.data;
    }
}

export const screenService = new ScreenService();
